import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Upload, GitCompare } from 'lucide-react';
import { aiAgentsService, AiAgent } from '../../api/services/aiAgents.service';
import { diffDocuments, preview, DocDiff } from './runtimeDocDiff';
import JsonTextArea, { useJsonCheck } from './JsonTextArea';

/**
 * Edit the agent document the runtime actually holds.
 *
 * <h3>Why this exists at all</h3>
 * The ordinary save overlays only the handful of fields cpass models onto the
 * stored document. That is what stops an adopted agent being silently rebuilt —
 * but it also means the model, the voice, the language and the whole
 * gemini_live block (VAD sensitivities, silence duration, ringback, welcome
 * delay) cannot be reached from the edit screen at all. Until now the only way
 * to change them was to POST the document by hand.
 *
 * <h3>Why the diff is not optional</h3>
 * A push meant to change ONE tool URL once rewrote 94 of 154 leaves of a live
 * agent, and was reported as two changes because it had been checked against
 * fourteen hand-picked fields. So this compares EVERY leaf against what the
 * runtime is holding right now, and will not enable the push button until that
 * comparison has been made and looked at.
 *
 * <h3>Why reformatting is an edit</h3>
 * The Format button in the box below does not have its own path back into this
 * component: it hands its result to the same `onText` a keystroke does. So a
 * reformat clears the diff for the same reason typing does, without this file
 * having to know the button exists — which is the only version of that rule
 * that cannot be forgotten later. A "3 leaves change" sitting beside
 * re-indented text would be exactly the stale reassurance the diff exists to
 * prevent.
 */
export default function RuntimeDocumentEditor({ agent, onPushed }: { agent: AiAgent; onPushed?: () => void }) {
  const [live, setLive] = useState<Record<string, any> | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [diff, setDiff] = useState<DocDiff | null>(null);

  const uuid = agent.agent_uuid;

  const loadLive = useCallback(async () => {
    if (!uuid) { setError('This agent has no agent_uuid, so it has no runtime document.'); setLoading(false); return; }
    setLoading(true); setError(null); setNote(null); setDiff(null);
    const r = await aiAgentsService.viewRuntimeAgent(uuid);
    if (r.success && r.data) {
      setLive(r.data);
      setText(JSON.stringify(r.data, null, 2));
    } else {
      setError(r.error || 'Could not read the document from the runtime');
    }
    setLoading(false);
  }, [uuid]);

  useEffect(() => { loadLive(); }, [loadLive]);

  const parsed = (): Record<string, any> | null => {
    try { return JSON.parse(text); } catch { return null; }
  };

  // Any edit invalidates a diff computed against the previous text — showing a
  // stale "3 leaves change" beside different content is worse than showing
  // none. Reformatting arrives here too; see the note at the top of the file.
  const onText = (v: string) => { setText(v); setDiff(null); setNote(null); };

  const jsonError = useJsonCheck(text);

  const compare = () => {
    const next = parsed();
    if (!next) { setError('That is not valid JSON, so there is nothing to compare.'); return; }
    setError(null);
    setDiff(diffDocuments(live, next));
  };

  const push = async () => {
    const next = parsed();
    if (!next || !diff) return;
    setPushing(true); setError(null); setNote(null);
    const r = await aiAgentsService.pushRuntimeDocument(agent.id!, next);
    if (r.success) {
      setNote(`Pushed. ${diff.total} leaf change${diff.total === 1 ? '' : 's'} sent.`);
      await loadLive();
      onPushed?.();
    } else {
      setError(r.error || 'Push failed');
    }
    setPushing(false);
  };

  // One parse per change rather than one per render, and it carries the
  // location the box needs to point at.
  const invalidJson = jsonError !== null;

  if (loading) return <div className="p-6 text-sm text-gray-500">Reading the document from the runtime…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          This replaces the agent's <strong>entire</strong> runtime document, exactly as written — it is not merged
          with what is there. Compare first: the diff below is against what the runtime is holding right now, and it
          checks every leaf, not a sample.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={loadLive} className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> Reload from runtime
        </button>
        <button onClick={compare} disabled={invalidJson}
                className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs disabled:opacity-40">
          <GitCompare className="w-3.5 h-3.5" /> Compare
        </button>
        <button onClick={push} disabled={!diff || diff.total === 0 || pushing || invalidJson}
                title={!diff ? 'Compare before pushing' : diff.total === 0 ? 'Nothing differs from the runtime' : undefined}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-40">
          <Upload className="w-3.5 h-3.5" /> {pushing ? 'Pushing…' : 'Push to runtime'}
        </button>
      </div>

      {error && <div className="rounded bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
      {note && <div className="rounded bg-green-50 dark:bg-green-900/20 px-3 py-2 text-xs text-green-700 dark:text-green-300">{note}</div>}

      {diff && (
        <div className="rounded border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 text-xs font-medium">
            {diff.total === 0
              ? `No differences — all ${diff.leavesCompared} leaves match the runtime.`
              : `${diff.total} of ${diff.leavesCompared} leaves differ — ${diff.changed.length} changed, ${diff.added.length} added, ${diff.removed.length} removed.`}
          </div>
          {diff.total > 0 && (
            <div className="max-h-72 overflow-auto p-2 font-mono text-[11px] leading-relaxed">
              {diff.removed.map(c => (
                <div key={`r${c.path}`} className="text-red-600 dark:text-red-400">− {c.path} = {preview(c.from)}</div>
              ))}
              {diff.changed.map(c => (
                <div key={`c${c.path}`} className="text-amber-700 dark:text-amber-400">~ {c.path}: {preview(c.from)} → {preview(c.to)}</div>
              ))}
              {diff.added.map(c => (
                <div key={`a${c.path}`} className="text-green-700 dark:text-green-400">+ {c.path} = {preview(c.to)}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <JsonTextArea value={text} onChange={onText} error={jsonError} height={448} />
    </div>
  );
}
